'use client';

import {
  Box,
  Button,
  Typography,
  SystemIcon,
  Autocomplete,
  TextFieldDropdown,
} from '@wishket/design-system';
import { FormEvent } from 'react';
import { twMerge } from 'tailwind-merge';

import { CAREER } from './ProfileInfoForm.constants';
import { LabeledContent, RoleSelect } from './ProfileInfoForm.parts';

import {
  RoleItem,
  LevelItem,
  CareerItem,
  SearchSkillItem,
  StaticSkillItem,
  RoleQueryResponse,
} from '@/shared/types';
import { Icon } from '@/shared/Components';
import { getScrollbarStyle, scrollToElementById } from '@/shared/utils';

interface ProfileInfoFormProps {
  error: {
    roleErrorMessage: string;
    levelErrorMessage: string;
    skillErrorMessage: string;
    careerErrorMessage: string;
  };
  skillData: {
    skillQuery: string;
    selectedSkill: string[];
    staticSkillItems: StaticSkillItem[];
    searchedSkillData?: SearchSkillItem[];
  };
  levelData: LevelItem[];
  isOnboarding?: boolean;
  selectedRole: RoleItem;
  selectedLevel: LevelItem;
  selectedCareer: CareerItem;
  roleData?: RoleQueryResponse;
  onClose?: () => void;
  skillInput: (value: string) => void;
  deleteStackChip: (item: string) => void;
  handleRoleChange: (item: RoleItem) => void;
  handleLevelChange: (item: LevelItem) => void;
  setSearchStackValue: (value: string) => void;
  handleCareerChange: (item: CareerItem) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

const ProfileInfoForm = ({
  error,
  roleData,
  skillData,
  levelData,
  selectedRole,
  selectedLevel,
  selectedCareer,
  isOnboarding = false,
  onClose,
  onSubmit,
  skillInput,
  deleteStackChip,
  handleRoleChange,
  handleLevelChange,
  handleCareerChange,
  setSearchStackValue,
}: ProfileInfoFormProps) => {
  const handleFocusSection = (id: string) => {
    scrollToElementById(id, 250);
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="features-profileModalContents"
      className="flex h-dvh w-full flex-col desktop:h-fit desktop:max-h-[80vh]"
    >
      {isOnboarding ? (
        <Box className="justify-start border-b px-5 py-6 font-medium text-w-gray-900 desktop:px-8">
          <Box className="flex items-center gap-1">
            <Typography variant="subTitle2">10초면 충분해요!</Typography>
            <Icon name="ok" />
          </Box>
          <Typography variant="subTitle2">
            딱 맞는 콘텐츠를 추천해 드릴게요.
          </Typography>
        </Box>
      ) : (
        <Box className="flex h-[60px] items-center justify-between border-b px-5 desktop:h-fit desktop:px-8 desktop:py-6">
          <Box className="block size-6 desktop:hidden" />
          <Typography
            variant="subTitle1"
            className="font-medium text-w-gray-900 desktop:typo-subTitle1"
          >
            프로필 수정
          </Typography>
          <button onClick={onClose}>
            <SystemIcon name="large_delete" className="text-w-gray-600" />
          </button>
        </Box>
      )}
      <Box
        className={twMerge(
          'flex flex-1 flex-col gap-8 overflow-y-scroll py-6 desktop:h-fit desktop:max-h-[584px]',
          getScrollbarStyle(),
          isOnboarding ? 'h-[calc(100dvh-186px)]' : 'h-[calc(100dvh-142px)]'
        )}
      >
        <LabeledContent
          title="직무"
          id="profile_modal_role"
          isError={!!error.roleErrorMessage}
          helperText="학생과 취업 준비생은 관심 직무를 선택해 주세요."
          onClick={() => handleFocusSection('profile_modal_role')}
        >
          <RoleSelect
            placeholder="직무를 선택해주세요"
            roleItems={roleData?.roles}
            selectedItem={selectedRole}
            error={!!error.roleErrorMessage}
            categories={roleData?.categories || []}
            recommendItems={roleData?.recommendRoles}
            handleChange={handleRoleChange}
          />
        </LabeledContent>
        <LabeledContent
          title="보유기술"
          id="profile_modal_skill"
          onFocus={() => handleFocusSection('profile_modal_skill')}
        >
          <Autocomplete
            placeholder="기술명을 입력해 주세요. 예) Excel"
            value={skillData?.skillQuery}
            isError={!!error.skillErrorMessage}
            skillItems={skillData?.staticSkillItems}
            selectedItems={skillData?.selectedSkill}
            hasIndexBar={skillData?.skillQuery === ''}
            legacySkillItems={skillData?.searchedSkillData || []}
            onInputItemClick={skillInput}
            onChipDelete={deleteStackChip}
            onValueChange={setSearchStackValue}
            onSkillItemClick={(e) => skillInput(e.value)}
            onLegacySkillItemClick={(e) => skillInput(e.value)}
          />
        </LabeledContent>
        <LabeledContent
          title="총 경력"
          id="profile_modal_career"
          onFocus={() => handleFocusSection('profile_modal_career')}
        >
          <TextFieldDropdown
            className="h-fit w-full"
            placeholder="총 경력을 선택해 주세요."
            items={CAREER}
            selectedItem={selectedCareer}
            isError={!!error.careerErrorMessage}
            onItemClick={handleCareerChange}
          />
        </LabeledContent>
        <LabeledContent
          title="레벨"
          id="profile_modal_level"
          helperText={
            error.levelErrorMessage
              ? ''
              : '주니어(1~3년), 미드레벨(4~8년), 시니어(9년~)'
          }
          onFocus={() => handleFocusSection('profile_modal_level')}
        >
          <TextFieldDropdown
            className="h-fit w-full"
            placeholder="레벨을 선택해 주세요."
            items={levelData}
            selectedItem={selectedLevel}
            isError={!!error.levelErrorMessage}
            errorMessage={error.levelErrorMessage}
            onItemClick={handleLevelChange}
          />
        </LabeledContent>
      </Box>
      <Box className="w-full justify-end border-t px-5 pb-4 pt-[15px] desktop:px-8 desktop:py-5">
        {isOnboarding ? (
          <Button variant="solid" className="w-full" type="submit">
            입력 완료
          </Button>
        ) : (
          <Box className="flex justify-end gap-4">
            <Button
              variant="solid"
              className="w-full border-w-bluegray-200 bg-w-bluegray-200 focus:bg-w-bluegray-300 active:bg-w-bluegray-300 hover:border-w-bluegray-300 hover:bg-w-bluegray-300 desktop:w-[89px] "
              type="button"
              onClick={onClose}
            >
              닫기
            </Button>
            <Button
              variant="solid"
              className="w-full desktop:w-[89px]"
              type="submit"
            >
              수정하기
            </Button>
          </Box>
        )}
      </Box>
    </form>
  );
};

export default ProfileInfoForm;
